import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { MatchStakeIcon } from '../components/MatchStakeLogo';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
        
        // Refresh user data to update email_verified status
        if (refreshUser) {
          await refreshUser();
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Failed to verify email. The link may be expired or invalid.');
      }
    };

    verifyEmail();
  }, [token, refreshUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center gap-2 justify-center mb-8">
          <MatchStakeIcon className="w-12 h-12" />
          <span className="font-heading font-bold text-2xl tracking-tight">MATCHSTAKE</span>
        </div>

        {/* Card */}
        <div className="glass-card rounded-2xl p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="w-20 h-20 rounded-full bg-[#00F0FF]/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold mb-2">Verifying Your Email</h1>
              <p className="text-white/50">Please wait while we verify your email address...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 rounded-full bg-[#00FF94]/20 flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle className="w-10 h-10 text-[#00FF94]" />
              </motion.div>
              <h1 className="font-heading text-2xl font-bold mb-2 text-[#00FF94]">Email Verified!</h1>
              <p className="text-white/50 mb-6">{message}</p>
              <p className="text-sm text-white/60 mb-6">
                You now have full access to MatchStake. Create matches, join competitions, and withdraw your winnings!
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full btn-primary"
                data-testid="go-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 rounded-full bg-[#FF2E2E]/20 flex items-center justify-center mx-auto mb-6"
              >
                <XCircle className="w-10 h-10 text-[#FF2E2E]" />
              </motion.div>
              <h1 className="font-heading text-2xl font-bold mb-2 text-[#FF2E2E]">Verification Failed</h1>
              <p className="text-white/50 mb-6">{message}</p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full btn-primary"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="w-full btn-secondary"
                >
                  Back to Login
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
